/**
 * User Settings API Client (IAM Self-Service)
 *
 * Thin client over the existing, already-built IAM self-service user-settings
 * endpoints (api/v1/iam/self-service/user-settings/*). Flow Studio uses this to
 * persist the signed-in user's own defaults — e.g. their Advanced Execute
 * observability choice, saved under the "UserObserveSettings" key (see
 * ObservabilityOverride in processEngineExecution.types.ts for the JSON shape
 * stored in SettingValue).
 *
 * This is a generic key/value settings store owned by IAM, not something specific
 * to observability — the SettingKey namespaces whatever JSON payload the caller owns.
 */

import { BaseApiClient, ApiResponse } from './baseApiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';

/**
 * A single user setting row.
 * Maps to C# UserSetting (IAM.Domain) — SettingValue carries an arbitrary
 * JSON-encoded string payload whose shape is owned by the caller (identified by SettingKey).
 */
export interface UserSetting {
  settingKey: string;
  settingValue: string;
}

const readField = (source: any, camelKey: string, pascalKey: string): any =>
  source?.[camelKey] ?? source?.[pascalKey];

export class UserSettingsApiClient extends BaseApiClient {
  /**
   * Look up the current user's setting by key.
   * POST /api/v1/iam/self-service/user-settings/by-key
   *
   * Returns null when no setting has been saved yet for this key — this is the
   * expected "no default yet" case (first-time use), not an error condition.
   */
  async getByKey(settingKey: string): Promise<UserSetting | null> {
    const response = await this.postQuery<any>(API_ENDPOINTS.USER_SETTINGS.BY_KEY, {
      settingKey,
    });

    if (!response.success || !response.data) {
      return null;
    }

    const settingValue = readField(response.data, 'settingValue', 'SettingValue');
    if (settingValue == null) {
      return null;
    }

    return {
      settingKey: readField(response.data, 'settingKey', 'SettingKey') ?? settingKey,
      settingValue,
    };
  }

  /**
   * Upsert the current user's setting for the given key.
   * POST /api/v1/iam/self-service/user-settings/set
   *
   * Uses postQuery (flat body: {metadata, settingKey, settingValue}), not post
   * (which nests the payload under a "data" key) — confirmed against the live
   * backend that the request DTO binds SettingKey/SettingValue at the root, the
   * same shape getByKey's postQuery call above already uses. post's nested shape
   * fails server-side validation ("Parameter 'SettingKey' cannot be null, empty,
   * or whitespace") because the binder never sees the nested properties.
   */
  async set(settingKey: string, settingValue: string): Promise<ApiResponse<UserSetting>> {
    return this.postQuery<UserSetting>(API_ENDPOINTS.USER_SETTINGS.SET, {
      settingKey,
      settingValue,
    });
  }
}

export const userSettingsApiClient = new UserSettingsApiClient();
